import React, { useEffect, useState } from 'react';
import { ComponentUpdateProps, ComponentSettingsLlm, TextBlockInitialDataResponse } from '@report-content';
import useReportContentContext from 'components/report-content/hooks/useReportContentContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reportAPI } from 'api/report';
import { LlmInitialDataResponse } from 'components/report-content/src/model/cmp/llm';
import MarkdownIt from 'markdown-it';
import useOnSVChange from 'components/report-content/hooks/useOnSVChange';
import useOnMeasurementTime from 'components/report-content/hooks/useOnMeasurementTime';
import useComponentReady from 'components/report-content/hooks/useComponentReady';
import useBundleTranslation from 'i18n';
import { Box } from '@mui/material';

const md = new MarkdownIt();
function LlmRow({ summary, details }: { summary?: string; details?: string }) {
    const { t } = useBundleTranslation('main');
    const [showDetails, setShowDetails] = useState(false);

    return (
        <Box>
            {summary && (
                <Box
                    dangerouslySetInnerHTML={{
                        __html: md.render(summary),
                    }}
                />
            )}
            {details && (
                <Box>
                    <Box className={'invert-link'} component={'span'} onClick={() => setShowDetails(!showDetails)}>
                        {t(showDetails ? 'hide_details' : 'show_details')}
                    </Box>
                </Box>
            )}
            {showDetails && details && (
                <Box
                    dangerouslySetInnerHTML={{
                        __html: md.render(details),
                    }}
                />
            )}
        </Box>
    );
}

export default function Llm({
    contentSettings,
    component,
    updateBlockSettings,
    actions,
    editPanel,
    blockId,
}: ComponentUpdateProps<ComponentSettingsLlm>) {
    const { t } = useBundleTranslation(['components/report-content']);
    const { filtersString } = useReportContentContext(() => removeInitialData());
    const queryClient = useQueryClient();
    const initialQueryKey = `report_block_initial_${component.datasetRendererBlockComponentId}`;
    const removeInitialData = () => queryClient.removeQueries({ queryKey: [initialQueryKey] });
    const { data: initialData } = useQuery<LlmInitialDataResponse, Error>({
        queryKey: [initialQueryKey],
        queryFn: () => {
            return reportAPI.getComponentData(component.settings, contentSettings, filtersString);
        },
    });

    // Reset all data on segment value change
    useOnSVChange(contentSettings.segmentValueId, removeInitialData);
    // Full reset on Measurement Interval Change
    useOnMeasurementTime(contentSettings.measurement_time, removeInitialData);

    useEffect(() => {
        if (!initialData) {
            return;
        }
        removeInitialData();
    }, [component.settings.prompt]);

    useComponentReady(initialData);

    let tmp: { [key: string]: any } = {};
    try {
        if (initialData?.response) tmp = JSON.parse(initialData?.response);
    } catch (e) {}

    return (
        <Box sx={{ px: 1.5, py: 1, '& p': { mx: 0, my: 0.5 } }}>
            {initialData && tmp?.result ? (
                <Box>
                    {tmp?.result &&
                        tmp?.result.map((row: any, key: number) => {
                            return <LlmRow key={'llm_row_' + key} summary={row?.summary} details={row?.details} />;
                        })}
                    {!(tmp?.result && tmp?.result.length) && (
                        <Box
                            dangerouslySetInnerHTML={{
                                __html: md.render(initialData?.response),
                            }}
                        />
                    )}
                </Box>
            ) : (
                <Box>{t('llm.ai_analysis_is_running___')}</Box>
            )}{' '}
        </Box>
    );
}
